/**
 * v-permission directive for role-based element visibility
 * 
 * Usage:
 * v-permission="'admin'" - Show only for admin role
 * v-permission="['admin', 'user']" - Show for admin OR user roles
 * v-permission="{ any: ['admin', 'moderator'] }" - Show for any of these roles
 * v-permission="{ all: ['admin', 'editor'] }" - Show for all of these roles
 * v-permission="{ authenticated: true }" - Show only for authenticated users
 */

import store from '@/store';

export default {
  mounted(el, binding) {
    
    // Function to check permissions
    const checkPermission = () => {
      const value = binding.value;
      const userRoles = store.getters.userRoles || [];
      const isAuthenticated = store.getters.isAuthenticated;
      
      let hasPermission = false;
      
      if (!value) {
        // No permission specified, show by default
        hasPermission = true;
      } else if (typeof value === 'string') {
        // Single role check
        hasPermission = userRoles.includes(value);
      } else if (Array.isArray(value)) {
        // Multiple roles (OR logic)
        hasPermission = value.some(role => userRoles.includes(role));
      } else if (typeof value === 'object') {
        // Complex permission object
        if (value.authenticated !== undefined) {
          hasPermission = value.authenticated === isAuthenticated;
        }
        
        if (value.any && hasPermission !== false) {
          hasPermission = value.any.some(role => userRoles.includes(role));
        }
        
        if (value.all && hasPermission !== false) {
          hasPermission = value.all.every(role => userRoles.includes(role));
        }
        
        if (value.role && hasPermission !== false) {
          hasPermission = userRoles.includes(value.role);
        }
      }
      
      // Show or hide element based on permission
      if (!hasPermission) {
        el.style.display = 'none';
        el.setAttribute('data-permission-hidden', 'true');
      } else {
        // Only restore display if it was hidden by this directive
        if (el.getAttribute('data-permission-hidden') === 'true') {
          el.style.display = '';
          el.removeAttribute('data-permission-hidden');
        }
      }
    };
    
    // Check permissions immediately
    checkPermission();
    
    // Store unwatch function on element for cleanup
    el._permissionUnwatch = store.watch(
      (state) => ({
        roles: state.userRoles,
        auth: state.isLoggedIn
      }),
      () => checkPermission(),
      { deep: true }
    );
  },
  
  updated(el, binding) {
    // Re-check permissions when binding value changes
    const checkPermission = () => {
      const value = binding.value;
      const userRoles = store.getters.userRoles || [];
      const isAuthenticated = store.getters.isAuthenticated;
      
      let hasPermission = false;
      
      if (!value) {
        hasPermission = true;
      } else if (typeof value === 'string') {
        hasPermission = userRoles.includes(value);
      } else if (Array.isArray(value)) {
        hasPermission = value.some(role => userRoles.includes(role));
      } else if (typeof value === 'object') {
        if (value.authenticated !== undefined) {
          hasPermission = value.authenticated === isAuthenticated;
        }
        
        if (value.any && hasPermission !== false) {
          hasPermission = value.any.some(role => userRoles.includes(role));
        }
        
        if (value.all && hasPermission !== false) {
          hasPermission = value.all.every(role => userRoles.includes(role));
        }
        
        if (value.role && hasPermission !== false) {
          hasPermission = userRoles.includes(value.role);
        }
      }
      
      if (!hasPermission) {
        el.style.display = 'none';
        el.setAttribute('data-permission-hidden', 'true');
      } else {
        if (el.getAttribute('data-permission-hidden') === 'true') {
          el.style.display = '';
          el.removeAttribute('data-permission-hidden');
        }
      }
    };
    
    checkPermission();
  },
  
  unmounted(el) {
    // Clean up watcher
    if (el._permissionUnwatch) {
      el._permissionUnwatch();
      delete el._permissionUnwatch;
    }
  }
};